import {useParams, useSearchParams} from "react-router-dom";
import data from "data/searchData.json";
import React, {useEffect, useMemo, useState} from "react";
import {Button, CardContent, IconButton, Link, Paper, Rating, Typography} from "@mui/material";
import {AssistantDirection, Bookmark, BookmarkBorder, Send} from '@mui/icons-material'
import "./SearchResult.css"
import {ReviewSection} from "components/review/ReviewSecton";
import {ListParkReviewsQuery, ListUserSavedParksQuery, ParkReview, UserSavedParks} from "API";
import {generateClient, GraphQLResult} from "aws-amplify/api";
import {listParkReviews, listUserSavedParks} from "graphql/queries";
import {AddPointSection} from "../../../components/point/AddPointSection";
import {deleteUserSavedParks} from "../../../graphql/mutations";
import {CreateBookMark} from "../../../components/saved-parks/CreateBookMark";
import {useRequireAuth} from "../../../services/auth/useRequireAuth";


export function SearchResult() {
  const {parkId} = useParams()
  const client = generateClient()
  const [params] = useSearchParams()
  const {authState, requireAuth, signInDialog} = useRequireAuth()
  const option = useMemo(() => data.find(v => v.id === parkId), [parkId])

  const [showCreateBookMark, setShowCreateBookMark] = useState(false)

  const [averageRating, setAverageRating] = useState<number | null>(null)
  const [bookMarkId, setBookMarkId] = useState<string>()
  const [bookMarkSaveLoading, setBookMarkSaveLoading] = useState<boolean>(false)


  const location = useMemo(() => {
    if (params) {
      const lat = params.get("lat")
      const lon = params.get("lon")
      const zoom = params.get("zoom")
      if (lat && lon && zoom) {
        return {lat: parseFloat(lat), lon: parseFloat(lon), zoom: parseFloat(zoom)}
      }
    }
    if (option && option.searchArea) {
      return {lat: option.searchArea.lat, lon: option.searchArea.lon, zoom: 13}
    }
  }, [params, option])


  // The average is derived from member-only reviews, so guests get no rating
  // rather than a misleading zero.
  useEffect(() => {
    if (authState !== "signedIn") {
      setAverageRating(null)
      return
    }

    const reviewsPromise = client.graphql({
      query: listParkReviews, variables: {
        filter: {
          parkId: {
            eq: parkId
          }
        }
      }
    }) as Promise<GraphQLResult<ListParkReviewsQuery>>

    reviewsPromise.then((res) => {
      const items = (res.data?.listParkReviews?.items ?? []).filter((item): item is ParkReview => Boolean(item))
      let sumRating: number | null = null
      items.forEach(item => sumRating = (sumRating || 0) + item.rating)
      setAverageRating(sumRating && items.length > 0 ? (sumRating / items.length) : null)
    })
  }, [authState, client, parkId])

  useEffect(() => {
    // Saved parks are owner-only; there is nothing for a guest to look up.
    if (authState !== "signedIn") {
      setBookMarkId(undefined)
      return
    }

    const userSavedPromise = client.graphql({
      query: listUserSavedParks, variables: {
        filter: {
          parkId: {
            eq: parkId
          }
        }
      }
    }) as Promise<GraphQLResult<ListUserSavedParksQuery>>

    userSavedPromise.then((res) => {
      const bookMark = (res.data?.listUserSavedParks?.items ?? []).filter((item): item is UserSavedParks => Boolean(item)).at(0)
      if(bookMark)
        setBookMarkId(bookMark.id)
    })
  }, [authState, client, parkId])

  if (!option || !parkId) {
    return <></>
  }

  const doBookmark = async () => {
    setBookMarkSaveLoading(true)
    if(bookMarkId) {
      await client.graphql({
        query: deleteUserSavedParks,
        variables: {
          input: {
            id: bookMarkId
          },
        },
      })
      setBookMarkId(undefined)
      setBookMarkSaveLoading(false)
    } else {
      setShowCreateBookMark(true)
    }
  }

  if (option.type === "area") {
    return <div className={'SearchResult'}>
      <div className={'SearchResult-content'}>
        <div>
          <Typography variant="h4" component="div" sx={{pt: 2}}>{option.name}</Typography>
          <Typography variant={"body1"} component={'div'} sx={{py: 2}}>
            {option.description}
          </Typography>
          <div className={'SearchResult-Cards'}>
            <Paper variant="outlined">
              <CardContent>
                <Typography variant="h6" component="div">Regulation:</Typography>
                <Typography variant="body2">{option.restrictionDescription || "Unknown"}</Typography>
              </CardContent>
            </Paper>
            {option.link && <Paper variant="outlined">
                <CardContent>
                    <Typography variant="h6" component="div">More Information</Typography>
                    <Link href={option.link}>
                        Here for more information of this area
                    </Link>
                </CardContent>
            </Paper>}
          </div>
        </div>
      </div>
    </div>
  }


  return <div className={'SearchResult'}>
    <div className={'SearchResult-content'}>
    <div>
      <div className={'SearchResult-header'}>

        <Typography variant="h4" component="div">{option.name}</Typography>
        <IconButton
          style={{padding: 0}}
          aria-label={authState === "guest" ? "Sign in to save this park" : (bookMarkId ? "Remove from saved parks" : "Save park")}
          size={'large'}
          disabled={bookMarkSaveLoading}
          onClick={() => requireAuth(doBookmark)}
        >
          {bookMarkId ? <Bookmark fontSize={"large"} /> : <BookmarkBorder fontSize={"large"} />}
        </IconButton>
      </div>
      {averageRating && <Rating name="average-rating" key={option.id} value={averageRating} readOnly precision={0.25}/>}

      <Typography variant={"body1"} component={'div'} sx={{py: 2}}>{option.description}</Typography>
      <div className={'SearchResult-Cards'}>
        <Paper variant="outlined">
          <CardContent>{Array.isArray(option.restrictionDescription) ? <>
            <Typography variant="h6" component="div">Regulations:</Typography>
            <div
              className={"SearchResult-multiRestrictionDescriptions"}>{option.restrictionDescription.map((desc, index) =>
              <div key={index}>
                <Typography variant="subtitle1" component="div">{desc.name}</Typography>
                <Typography variant="body2">{desc.value}</Typography>
              </div>)}
            </div>
          </> : <>
            <Typography variant="h6" component="div">Regulation:</Typography>
            <Typography variant="body2">{option.restrictionDescription || "Unknown"}</Typography>
          </>}</CardContent>
        </Paper>
        {option.information && <Paper variant="outlined">
            <CardContent>
                <>
                    <Typography variant="h6" component="div">Properties:</Typography>
                    <div className={'SearchResult-information'}>{option.information.map((info, index) => <div
                      key={index}>
                      <Typography variant="subtitle1" component="div">{info.name}</Typography>
                      <Typography variant="body2">{info.value}</Typography>
                    </div>)}
                    </div>
                </>
            </CardContent>
        </Paper>}

      </div>
    </div>
    <div className={'SearchResult-buttonGroup'}>
      <Button
        variant="contained"
        size={'large'}
        endIcon={<Send/>}
        onClick={async () => {
          await navigator.share({url: document.location.href, title: "Dog Zones", text: option.name})
        }}
      >
        Share
      </Button>
      <Button
        variant="contained"
        size={'large'}
        endIcon={<AssistantDirection/>}
        onClick={() => {
          if (location) {
            window.open(`https://maps.google.com?q=${location.lat},${location.lon}`)
          }
        }}
      >
        Navigate
      </Button>
    </div>
    <AddPointSection parkId={option.id} location={location}/>
    <ReviewSection parkId={option.id} name={option.name}/>
    {showCreateBookMark && <CreateBookMark parkId={parkId} onSaved={(id) => setBookMarkId(id)} onClose={() => {
      setShowCreateBookMark(false)
      setBookMarkSaveLoading(false)
    }} />}
    {signInDialog}
    </div>
  </div>
}
